import DOMPurify from 'dompurify';
import { ReactElement } from 'react';
import { Button, FlexWrapper, FontIcon, InputField, Text } from '@intigral/shared';
import { Modal } from '../Modal';
import styles from './search.module.sass';

type SuggestionsType = string[] | { title: string; formatted: string }[];

export interface ISearch {
    open?: boolean;
    onOpenChange: () => void;
    query: string;
    setQuery: (query: string) => void;
    historyHeaderText?: string;
    queriesHistory?: string[];
    suggestions?: SuggestionsType;
    children?: ReactElement | string;
    handleSearchFormSubmit?: (query: string) => void;
    ref?: any;
    clearLabel?: string;
    /**
     * Error message to display when search is not successful, e.g., when query string is too short.
     */
    searchError?: string;
    onRecentSearchClear?: () => void;
}

export const Search = ({
    open,
    onOpenChange,
    query,
    setQuery,
    historyHeaderText,
    queriesHistory = [],
    suggestions = [],
    children,
    handleSearchFormSubmit,
    ref,
    clearLabel,
    searchError,
    onRecentSearchClear,
}: ISearch) => {
    /**
     * Renders a list of query suggestions or recent search queries as clickable elements.
     *
     * Each query is displayed with an icon and formatted text. When a query is clicked,
     * it triggers the `handleSearchFormSubmit` callback with the query value.
     *
     * @param queries - An array of query strings or objects of type `SuggestionsType`.
     *                  If an object, it should have `title` and `formatted` properties.
     * @param iconName - The name of the icon to display next to each query.
     * @returns An array of React elements representing the queries.
     */
    const renderQueriesList = (queries: string[] | SuggestionsType, iconName: string) => {
        return queries.length
            ? queries.map((q, idx) => {
                  const query = typeof q === 'string' ? q : q.title;
                  const highlightedQuery = typeof q === 'string' ? q : q.formatted;
                  return (
                      <span
                          className={styles['query']}
                          onClick={(e) => {
                              e.preventDefault();
                              handleSearchFormSubmit && handleSearchFormSubmit(query);
                          }}
                          key={query + idx}
                      >
                          <FontIcon name={iconName as any} color="gray" />
                          <Text as="h4" variant="h4" alignment="start" margin="0" fontWeight="regular">
                              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlightedQuery) }} />
                          </Text>
                      </span>
                  );
              })
            : null;
    };

    return (
        <Modal
            open={open}
            onOpenChange={onOpenChange}
            className={styles['search-wrapper']}
            closePlacement="null"
            widthSet="fullPage"
        >
            <div className={styles.search}>
                <Button small round secondary onClick={onOpenChange}>
                    <FontIcon name="arrow" className={styles['search-close-icon']} />
                </Button>
                <FlexWrapper alignItems="center" gap="2x" flexDirection="column">
                    <InputField
                        label=""
                        preIcon="search"
                        onChange={(e) => {
                            setQuery(e.target.value);
                        }}
                        icon={
                            query.length ? (
                                <Button
                                    plain
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setQuery('');
                                    }}
                                    className={styles.close}
                                >
                                    <span style={{ fontWeight: 'normal' }}>{clearLabel}</span>
                                    <FontIcon name="close" />
                                </Button>
                            ) : (
                                <></>
                            )
                        }
                        className={styles['search-input']}
                        autoFocus
                        value={query}
                        id="head-search-input"
                        ref={ref}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                if (handleSearchFormSubmit) {
                                    handleSearchFormSubmit((e.target as HTMLInputElement).value);
                                }
                            }
                        }}
                    />
                </FlexWrapper>
                {searchError && (
                    <Text
                        as="span"
                        alignment="start"
                        className={styles['error']}
                        color="gray"
                        size="14"
                        margin="0"
                        style={{ display: 'block' }}
                    >
                        {searchError}
                    </Text>
                )}
                <div className={styles['search-results']}>
                    {!children && !suggestions.length && queriesHistory.length ? (
                        <FlexWrapper
                            flexDirection="row"
                            gap="4x"
                            justifyContent="between"
                            alignItems="center"
                            paddingTop="2x"
                        >
                            <Text as="h4" variant="h4" alignment="start">
                                {historyHeaderText}
                            </Text>
                            <Text as="span" color="brand" onClick={onRecentSearchClear} style={{ cursor: 'pointer' }}>
                                {clearLabel}
                            </Text>
                        </FlexWrapper>
                    ) : null}
                    {suggestions.length
                        ? renderQueriesList(suggestions, 'search')
                        : renderQueriesList(queriesHistory, 'switcher')}
                    {children || null}
                </div>
            </div>
        </Modal>
    );
};
